import React, {
  useCallback,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import Container from '../Container';

const Carousel = forwardRef(
  (
    {
      onInit,
      initialSlide,
      onSlideStart,
      onSlideEnd,
      children,
      duration,
      transitionDuration,
      touchSwipe,
      ...props
    },
    ref
  ) => {
    const containerRef = useRef();
    const activeSlide = useRef(initialSlide);
    const durationTimeout = useRef();
    const slideStartTimeout = useRef();
    const translateX = useRef(0);
    const prevPageX = useRef();
    const prevPageY = useRef();
    const distanceX = useRef();
    const direction = useRef('left');

    const totalDuration = duration + transitionDuration;

    const onChange = () => {
      clearTimeout(slideStartTimeout.current);
      onSlideEnd(activeSlide.current - 1);
      slideStartTimeout.current = setTimeout(() => {
        onSlideStart(activeSlide.current);
      }, transitionDuration);
    };

    const translate = x => {
      const { style } = containerRef.current;

      translateX.current = x;

      window.requestAnimationFrame(() => {
        style.transform = `translateX(${x}px)`;
      });
    };

    const slideToIndex = i => {
      const { clientWidth, style } = containerRef.current;
      style.transition = `all ${transitionDuration}ms ease`;
      activeSlide.current = i;
      translate(-(i * clientWidth));
      onChange();
    };

    const next = () => {
      if (activeSlide.current >= children.length - 1) {
        return;
      }
      slideToIndex(activeSlide.current + 1);
      clearTimeout(durationTimeout.current);
      durationTimeout.current = setTimeout(() => {
        next();
      }, totalDuration);
    };

    const play = () => {
      clearTimeout(durationTimeout.current);
      durationTimeout.current = setTimeout(() => {
        next();
      }, totalDuration);
    };

    const stop = () => {
      clearTimeout(durationTimeout.current);
    };

    const prev = () => {
      if (!activeSlide.current) {
        return;
      }
      slideToIndex(activeSlide.current - 1);
      play();
    };

    const goTo = i => {
      slideToIndex(i);
      play();
    };

    const init = useCallback(() => {
      const {
        current: { clientWidth },
      } = containerRef;

      activeSlide.current = initialSlide;
      translate(-(clientWidth * initialSlide));
      onSlideStart(initialSlide);
      durationTimeout.current = setTimeout(() => {
        next();
      }, duration);
      onInit();
    }, [initialSlide]);

    useEffect(() => {
      const resize = () => {
        const {
          current: { clientWidth },
        } = containerRef;
        translate(-(clientWidth * activeSlide.current));
      };

      window.addEventListener('resize', resize);

      return () => {
        window.removeEventListener('resize', resize);
      };
    }, []);

    useEffect(() => {
      init();
    }, [init]);

    const lockSlide = () => {
      const { clientWidth } = containerRef.current;

      const isFirstSlide = activeSlide.current === 0;
      const isLastSlide = activeSlide.current === children.length - 1;
      const adequateSwipeDistance = Math.abs(distanceX.current) > 5;

      if (
        direction.current === 'left' &&
        adequateSwipeDistance &&
        !isFirstSlide
      ) {
        slideToIndex(activeSlide.current - 1);
        return;
      }

      if (
        direction.current === 'right' &&
        adequateSwipeDistance &&
        !isLastSlide
      ) {
        slideToIndex(activeSlide.current + 1);
        return;
      }

      translate(-(clientWidth * activeSlide.current));
      direction.current = null;
    };

    const onTouchStart = () => {
      if (!touchSwipe) {
        return;
      }
      const { style } = containerRef.current;
      style.transition = 'none';
      stop();
    };

    const onTouchMove = e => {
      if (!touchSwipe) {
        return;
      }
      const {
        touches: [touch],
      } = e;

      const { pageX, pageY } = touch;

      if (prevPageX.current) {
        distanceX.current = pageX - prevPageX.current;
        const distanceY = pageY - prevPageY.current;

        const isHorizontal = Math.abs(distanceX.current) > Math.abs(distanceY);

        if (isHorizontal) {
          direction.current = distanceX.current < 0 ? 'right' : 'left';
          translate(translateX.current + distanceX.current);
        } else {
          direction.current = distanceY < 0 ? 'down' : 'up';
        }
      }

      prevPageX.current = pageX;
      prevPageY.current = pageY;
    };

    const onTouchEnd = () => {
      if (!touchSwipe) {
        return;
      }
      const { style } = containerRef.current;
      style.transition = `all ${transitionDuration}ms ease`;
      prevPageX.current = 0;
      prevPageY.current = 0;
      lockSlide();
      play();
    };

    useImperativeHandle(ref, () => ({
      stop,
      play,
      next,
      prev,
      goTo,
    }));

    useEffect(() => {
      return () => {
        clearTimeout(durationTimeout.current);
        clearTimeout(slideStartTimeout.current);
      };
    }, []);

    return (
      <Container overflow="hidden" {...props}>
        <Container
          ref={containerRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          display="flex"
          flex="1"
          minHeight="0"
        >
          {children}
        </Container>
      </Container>
    );
  }
);

Carousel.propTypes = {
  onInit: PropTypes.func,
  onSlideStart: PropTypes.func,
  onSlideEnd: PropTypes.func,
  initialSlide: PropTypes.number,
  children: PropTypes.node.isRequired,
  duration: PropTypes.number,
  transitionDuration: PropTypes.number,
  touchSwipe: PropTypes.bool,
};

Carousel.defaultProps = {
  onInit: () => {},
  onSlideStart: () => {},
  onSlideEnd: () => {},
  initialSlide: 0,
  duration: 10000,
  transitionDuration: 500,
  touchSwipe: true,
};

export default Carousel;
